import React, { useState, useEffect } from 'react';
import {
    MdPeople,
    MdAssignment,
    MdTrendingUp,
    MdStar,
    MdCalendarToday,
    MdMoreVert,
    MdArrowForward,
    MdRefresh
} from 'react-icons/md';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    LineChart,
    Line
} from 'recharts';
import Cookies from 'js-cookie';
import axios from 'axios';
import API_BASE_URL from '../../../config/api'
// import './Dashboard.css';
import Topbar from '../layouts/Topbar';
import Sidebar from '../layouts/Sidebar';
import { Link } from 'react-router-dom';
import './Dropdown.css';
import { FaEye } from 'react-icons/fa';

const Dashboard = () => {
    const [loading, setLoading] = useState(true);
    const [performanceData, setPerformanceData] = useState(null);
    const [statusSummary, setStatusSummary] = useState({ status_distribution: [] });
    const [statusLoading, setStatusLoading] = useState(true);
    const [statusError, setStatusError] = useState(null);
    const baseUrl = API_BASE_URL
    const [refreshing, setRefreshing] = useState(false);
    const fetchRef = React.useRef();
    const [trendYearDropdownOpen, setTrendYearDropdownOpen] = useState(false);
    const [trendSelectedYear, setTrendSelectedYear] = useState(new Date().getFullYear());
    const [trendDataYear, setTrendDataYear] = useState(new Date().getFullYear());
    const trendDropdownRef = React.useRef(null);

    // Recent reviews state (match EmployeeDashboard behavior)
    const [loadingReviews, setLoadingReviews] = useState(true);
    const [reviewsError, setReviewsError] = useState(null);
    const [reviews, setReviews] = useState([]);

    const fetchData = React.useCallback(async (showLoading = true, year = trendDataYear) => {
        if (showLoading) setLoading(true);
        setRefreshing(true);
        try {
            const token = Cookies.get('token');
            const response = await axios.get(`${baseUrl}/api/performance-analytics?year=${year}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });
            setPerformanceData(response.data);
        } catch (err) {
            setPerformanceData(null);
        } finally {
            if (showLoading) setLoading(false);
            setRefreshing(false);
        }
    }, [baseUrl, trendDataYear]);

    // Fetch status_distribution from /api/reporting/summary
    const fetchStatusSummary = React.useCallback(async () => {
        setStatusLoading(true);
        setStatusError(null);
        try {
            const token = Cookies.get('token');
            const response = await axios.get(`${baseUrl}/api/reporting/summary`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });
            setStatusSummary(response.data);
        } catch (err) {
            setStatusError('Failed to load status distribution');
            setStatusSummary({ status_distribution: [] });
        } finally {
            setStatusLoading(false);
        }
    }, [baseUrl]);

    // Fetch recent reviews like EmployeeDashboard
    useEffect(() => {
        const fetchReviews = async () => {
            setLoadingReviews(true);
            setReviewsError(null);
            try {
                const token = Cookies.get('token');
                const currentYear = new Date().getFullYear();
                const response = await axios.get(`${baseUrl}/api/goals/`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                    params: { year: currentYear }
                });
                const completed = (Array.isArray(response.data) ? response.data : [])
                    .filter(g => (g.status === 'complete' || g.status === 'completed') && Number(g.hod_score) > 0);
                completed.sort((a, b) => new Date(b.updated_at || b.completed_at || b.created_at) - new Date(a.updated_at || a.completed_at || a.created_at));
                setReviews(completed);
            } catch (err) {
                setReviewsError('Failed to fetch recent reviews.');
            } finally {
                setLoadingReviews(false);
            }
        };
        fetchReviews();
    }, []);

    // Initial fetch and polling
    React.useEffect(() => {
        fetchData(true, trendDataYear);
        fetchStatusSummary();
        fetchRef.current = setInterval(() => {
            fetchData(false, trendDataYear);
            fetchStatusSummary();
        }, 30000);
        return () => clearInterval(fetchRef.current);
    }, [fetchData, fetchStatusSummary, trendDataYear]);

    // Close dropdown on outside click
    React.useEffect(() => {
        function handleClickOutside(event) {
            if (trendDropdownRef.current && !trendDropdownRef.current.contains(event.target)) {
                setTrendYearDropdownOpen(false);
            }
        }
        if (trendYearDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [trendYearDropdownOpen]);

    const COLORS = ['#4F46E5', '#818CF8', '#A5B4FC', '#C7D2FE'];

    // Transform quarterly_trends object to array for recharts
    const quarterlyTrendsData = React.useMemo(() => {
        if (!performanceData?.quarterly_trends) return [];
        return Object.entries(performanceData.quarterly_trends).map(([quarter, stats]) => ({
            quarter,
            ...stats
        }));
    }, [performanceData]);

    // Prepare data for reporting summary pie chart
    const reportingPieData = [
        { name: 'Reported', value: statusSummary.reported_goals },
        { name: 'Not Reported', value: statusSummary.not_reported }
    ];
    const reportingPieColors = ['#4F46E5', '#ef4444'];

    // Custom label for pie chart to avoid cut-off
    const renderPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index, name }) => {
        const RADIAN = Math.PI / 180;
        const radius = outerRadius + 18;
        const x = cx + radius * Math.cos(-midAngle * RADIAN);
        const y = cy + radius * Math.sin(-midAngle * RADIAN);
        return (
            <text x={x} y={y} fill="#374151" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" fontSize={13} fontWeight={600}>
                {`${name}: ${(percent * 100).toFixed(0)}%`}
            </text>
        );
    };

    const StatCard = ({ icon: Icon, title, value, trend, loading, showArrow, linkTo }) => (
        <div className="stat-card">
            {loading ? (
                <Skeleton height={100} />
            ) : (
                <>
                    <div className="stat-icon">
                        <Icon />
                    </div>
                    <div className="stat-content">
                        <h3>{title}</h3>
                        <div className="stat-value">{value}</div>
                        {trend && <div className="stat-trend">{trend}</div>}
                    </div>
                    {showArrow && linkTo && (
                        <Link to={linkTo} className="stat-arrow">
                            <MdArrowForward />
                        </Link>
                    )}
                </>
            )}
        </div>
    );

    const ChartCard = ({ title, children, loading, moreBtnProps, dropdown }) => (
        <div className="chart-card">
            {loading ? (
                <Skeleton height={300} />
            ) : (
                <>
                    <div className="chart-header" style={{ position: 'relative' }}>
                        <h3>{title}</h3>
                        <button className="more-btn" {...moreBtnProps}>
                            <MdMoreVert />
                        </button>
                        {dropdown}
                    </div>
                    <div className="chart-content">
                        {children}
                    </div>
                </>
            )}
        </div>
    );

    const totalGoals = React.useMemo(() => {
        return statusSummary?.status_distribution?.reduce((sum, item) => sum + item.count, 0) || 0;
    }, [statusSummary]);

    // RecentEvaluations component (match EmployeeDashboard)
    const RecentEvaluations = ({ reviews }) => {
        return (
            <div className="recent-evaluations-card" style={{ background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', padding: 24, marginTop: 32 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                    <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>Recent Reviews</h2>
                    
                </div>
                {loadingReviews ? (
                    <div style={{ color: '#888', textAlign: 'center', padding: 32 }}>Loading...</div>
                ) : reviewsError ? (
                    <div style={{ color: '#ef4444', textAlign: 'center', padding: 32 }}>{reviewsError}</div>
                ) : (!reviews || reviews.length === 0) ? (
                    <div style={{ color: '#888', textAlign: 'center', padding: 32 }}>No recent reviews found.</div>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ background: '#f3f4f6' }}>
                                    <th style={{ textAlign: 'left', padding: '10px 8px', fontWeight: 600, fontSize: 15 }}>Activity / KPI</th>
                                    <th style={{ textAlign: 'left', padding: '10px 8px', fontWeight: 600, fontSize: 15 }}>Performance Standard / Target</th>
                                    <th style={{ textAlign: 'left', padding: '10px 8px', fontWeight: 600, fontSize: 15 }}>Metric</th>
                                    <th style={{ textAlign: 'left', padding: '10px 8px', fontWeight: 600, fontSize: 15 }}>Self Score</th>
                                    <th style={{ textAlign: 'left', padding: '10px 8px', fontWeight: 600, fontSize: 15 }}>HOD Score</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reviews.slice(0, 5).map((review, idx) => (
                                    <tr key={review.id || idx} style={{ borderBottom: '1px solid #e5e7eb' }}>
                                        <td style={{ padding: '10px 8px', maxWidth: 220, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{review.activity || '-'}</td>
                                        <td style={{ padding: '10px 8px', maxWidth: 300, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{review.performance_standard || '-'}</td>
                                        <td style={{ padding: '10px 8px', maxWidth: 300, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{review.metric || '-'}</td>
                                        <td style={{ padding: '10px 8px', color: '#4F46E5', fontWeight: 600 }}>{review.score != null ? review.score : '-'}</td>
                                        <td style={{ padding: '10px 8px', maxWidth: 300, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{review.hod_score != null ? review.hod_score : '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        );
    };

    return (
        <>
            <Topbar />
            <Sidebar />
            <div className="dashboard-container">
                <div className="dashboard-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <h1 style={{ margin: 0 }}>Performance Dashboard</h1>
                        <button
                            className="refresh-btn"
                            onClick={() => fetchData()}
                            disabled={refreshing}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' }}
                            title="Refresh"
                        >
                            <MdRefresh style={{ fontSize: '1.5rem', color: refreshing ? '#aaa' : '#4F46E5', transition: 'transform 0.3s', transform: refreshing ? 'rotate(360deg)' : 'none' }} />
                        </button>
                    </div>
                    <p style={{ margin: 0 }}>Overview of employee performance metrics and KPIs</p>
                </div>

                <div className="stats-grid">
                    <StatCard
                        icon={MdPeople}
                        title="Total Employees"
                        value={performanceData?.overall_statistics.total_employees}
                        loading={loading}
                        showArrow={true}
                        linkTo="/hr/employees"
                    />
                    <StatCard
                        icon={MdAssignment}
                        title="Departments"
                        value={performanceData?.overall_statistics?.total_departments}
                        loading={loading}
                        showArrow={true}
                        linkTo="/hr/departments"
                    />
                    <StatCard
                        icon={MdCalendarToday}
                        title="Cost Centers"
                        value={performanceData?.overall_statistics?.total_cost_centers}
                        loading={loading}
                        showArrow={true}
                        linkTo="/hr/cost-centers"
                    />
                    <StatCard
                        icon={MdTrendingUp}
                        title="Avg Performance"
                        value={performanceData?.overall_statistics?.average_progress ? `${performanceData.overall_statistics?.average_progress}%` : '0%'}
                        loading={loading}
                        showArrow={false}
                    />
                </div>

                <div className="charts-grid">
                    <ChartCard title="Reporting Summary" loading={loading}>
                        <ResponsiveContainer width="100%" height={220}>
                            <PieChart>
                                <Pie
                                    data={reportingPieData}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={60}
                                    outerRadius={80}
                                    fill="#8884d8"
                                    paddingAngle={5}
                                    dataKey="value"
                                    labelLine={true}
                                    label={renderPieLabel}
                                >
                                    {reportingPieData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={reportingPieColors[index % reportingPieColors.length]} />
                                    ))}
                                </Pie>
                                <Tooltip />
                            </PieChart>
                        </ResponsiveContainer>
                    </ChartCard>

                    <ChartCard title="Goal Status Distribution" loading={statusLoading}>
                        <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                                <Pie
                                    data={statusSummary?.status_distribution || []}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={60}
                                    outerRadius={80}
                                    fill="#8884d8"
                                    paddingAngle={5}
                                    dataKey="count"
                                    nameKey="status"
                                    label={({ status }) => status}
                                >
                                    {(statusSummary?.status_distribution || []).map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip />
                                {/* Total Goals in center */}
                                {!(statusLoading || statusError) && (
                                    <text
                                        x={"50%"}
                                        y={"50%"}
                                        textAnchor="middle"
                                        dominantBaseline="middle"
                                        fontSize="1.5rem"
                                        fontWeight="bold"
                                        fill="#4F46E5"
                                    >
                                        {totalGoals}
                                        <tspan x="50%" dy="1.5em" fontSize="1rem" fontWeight="normal" fill="#333">Total Goals</tspan>
                                    </text>
                                )}
                                {statusError && <div style={{ color: 'red', textAlign: 'center', marginTop: 10 }}>{statusError}</div>}
                            </PieChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </div>

                <RecentEvaluations reviews={reviews} />
            </div>
        </>
    );
};

export default Dashboard;
